/**
 * outbox.ts — enqueue/dequeue pending mutations for POST /sync.
 *
 * One row per report (keyPath = localId). An edit of the same report
 * overwrites the previous enqueue — last-write-wins locally, matching the
 * server's updatedAt strategy.
 *
 * resetInflight() MUST be called on app startup to recover items left
 * inflight by a previous session that crashed (crash recovery).
 *
 * Fase 3: exponential backoff + terminal state.
 *   - MAX_ATTEMPTS: after this many failures, syncState → "failed" (no auto-retry).
 *   - backoffMs(attempts): exponential delay before re-trying an error item.
 *   - resetFailedItems(): resets all "failed" items to "pending" for a manual retry.
 */

import { getDb } from "../stores/db.js";
import type { OutboxItem, SyncInformeItem } from "../types.js";

const STORE = "outbox";

// ── Fase 3: backoff + terminal ─────────────────────────────────────────────────

/** Maximum number of auto-retry attempts before an item is promoted to "failed". */
const MAX_ATTEMPTS = 5;

/** Minimum delay between retries (base for exponential backoff). */
const BASE_BACKOFF_MS = 30_000; // 30 seconds

/** Upper bound for any single backoff window. */
const MAX_BACKOFF_MS = 300_000; // 5 minutes

/**
 * Returns the backoff delay in ms for the given attempt count.
 * Formula: min(2^attempts * BASE_BACKOFF_MS, MAX_BACKOFF_MS).
 *
 * Examples:
 *   attempts=1 → 60 s
 *   attempts=2 → 120 s (2 min)
 *   attempts=3 → 240 s (4 min)
 *   attempts=4+ → 300 s (5 min, capped)
 */
function backoffMs(attempts: number): number {
  return Math.min(Math.pow(2, attempts) * BASE_BACKOFF_MS, MAX_BACKOFF_MS);
}

/**
 * Enqueue a mutation. Upserts by localId — an edit of the same report
 * overwrites the previous outbox entry (last-write-wins).
 *
 * Read + write run inside a single readwrite transaction to prevent a
 * race where two concurrent enqueue() calls both read "no existing row"
 * and produce duplicate or inconsistent writes.
 *
 * On re-enqueue, attempts is reset to 0: the edit is a fresh mutation,
 * not a continuation of the failed attempt.
 * // ponytail: resetting attempts on re-enqueue drops backoff carry-over;
 * // upgrade = preserve attempts across re-enqueue if exponential backoff is added.
 */
export async function enqueue(item: SyncInformeItem): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORE, "readwrite");
  const existing = (await tx.store.get(item.localId)) as OutboxItem | undefined;
  const outboxItem: OutboxItem = {
    ...item,
    syncState: "pending",
    // Fresh mutation — reset attempts, backoff, and error so the new edit
    // starts clean. Any previous failure state is irrelevant: the edit is new.
    attempts: 0,
    enqueuedAt: existing?.enqueuedAt ?? new Date().toISOString(),
    lastError: undefined,
    lastAttemptAt: undefined,
  };
  await tx.store.put(outboxItem);
  await tx.done;
}

/**
 * Read all outbox items eligible for the next flush: syncState 'pending' OR
 * 'error' whose backoff window has elapsed.
 *
 * Exclusions:
 *   - 'inflight': recovered to 'pending' by resetInflight() on startup.
 *   - 'failed': reached MAX_ATTEMPTS — held for manual retry only.
 *   - 'error' in backoff window: skipped until the retry delay elapses.
 *
 * Fase 3: error items are gated by exponential backoff (backoffMs(attempts)).
 * An item is skipped when lastAttemptAt + backoffMs(attempts) > now.
 */
export async function getPending(): Promise<OutboxItem[]> {
  const db = await getDb();
  const tx = db.transaction(STORE, "readonly");
  const idx = tx.store.index("by-syncState");
  const pending = (await idx.getAll("pending")) as OutboxItem[];
  const errored = (await idx.getAll("error")) as OutboxItem[];
  await tx.done;

  // Filter out error items that are still within their backoff window.
  const now = Date.now();
  const eligibleErrored = errored.filter((item) => {
    // No backoff info → include immediately (safe default).
    if (item.attempts === 0 || !item.lastAttemptAt) return true;
    const elapsed = now - new Date(item.lastAttemptAt).getTime();
    return elapsed >= backoffMs(item.attempts);
  });

  return [...pending, ...eligibleErrored];
}

/** Mark a batch of items as inflight before sending them to /sync.
 *  Stamps `lastAttemptAt` so backoff timing is anchored to the send time.
 */
export async function markInflight(localIds: string[]): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORE, "readwrite");
  const now = new Date().toISOString();
  for (const localId of localIds) {
    const item = (await tx.store.get(localId)) as OutboxItem | undefined;
    if (item) {
      await tx.store.put({ ...item, syncState: "inflight", lastAttemptAt: now });
    }
  }
  await tx.done;
}

/**
 * Mark one item as error, record the message, and increment attempts.
 *
 * Fase 3: also stamps `lastAttemptAt` for backoff calculation, and promotes
 * the item to `"failed"` when `attempts + 1 >= MAX_ATTEMPTS`. Failed items are
 * excluded from `getPending()` auto-retry and require a manual "Reintentar"
 * (which calls `resetFailedItems()` before the next flush).
 */
export async function markError(localId: string, error: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORE, "readwrite");
  const item = (await tx.store.get(localId)) as OutboxItem | undefined;
  if (item) {
    const newAttempts = item.attempts + 1;
    const newSyncState: OutboxItem["syncState"] =
      newAttempts >= MAX_ATTEMPTS ? "failed" : "error";
    await tx.store.put({
      ...item,
      syncState: newSyncState,
      lastError: error,
      attempts: newAttempts,
      lastAttemptAt: new Date().toISOString(),
    });
  }
  await tx.done;
}

/** Remove one item from the outbox (called after successful reconciliation). */
export async function remove(localId: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORE, localId);
}

/**
 * On app startup: reset any inflight items back to pending.
 * Handles the case where a previous flush was interrupted (crash/reload)
 * leaving items stuck in syncState='inflight'.
 *
 * Fase 3: clears `lastAttemptAt` on recovery so crash-recovered items are not
 * held back by the backoff window from the aborted attempt. The attempts count
 * is preserved (they did fail, even if the outcome was unknown).
 */
export async function resetInflight(): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORE, "readwrite");
  const idx = tx.store.index("by-syncState");
  const inflight = (await idx.getAll("inflight")) as OutboxItem[];
  for (const item of inflight) {
    await tx.store.put({ ...item, syncState: "pending", lastAttemptAt: undefined });
  }
  await tx.done;
}

/**
 * Fase 3: return all outbox items in the terminal `"failed"` state.
 *
 * These items exceeded MAX_ATTEMPTS and are excluded from `getPending()` auto-retry.
 * They are surfaced in the SyncStatusPanel as "necesita atención" and can only be
 * retried manually by calling `resetFailedItems()` before the next flush.
 */
export async function getTerminalFailed(): Promise<OutboxItem[]> {
  const db = await getDb();
  const tx = db.transaction(STORE, "readonly");
  const idx = tx.store.index("by-syncState");
  const failed = (await idx.getAll("failed")) as OutboxItem[];
  await tx.done;
  return failed;
}

/**
 * Fase 3: reset all terminal `"failed"` items back to `"pending"` for a manual retry.
 *
 * Clears `attempts`, `lastError`, and `lastAttemptAt` so the items start fresh.
 * Intended to be called immediately before `flushRun("manual")` when the user
 * clicks "Reintentar" in the SyncStatusPanel — this makes the failed items
 * eligible for the next flush without altering the auto-retry behavior of
 * normal `"error"` items.
 */
export async function resetFailedItems(): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORE, "readwrite");
  const idx = tx.store.index("by-syncState");
  const failed = (await idx.getAll("failed")) as OutboxItem[];
  for (const item of failed) {
    await tx.store.put({
      ...item,
      syncState: "pending",
      attempts: 0,
      lastError: undefined,
      lastAttemptAt: undefined,
    });
  }
  await tx.done;
}

/**
 * Clear the backoff window on all `"error"` items so the very next flush retries
 * them immediately instead of waiting out the exponential delay.
 *
 * Used by the manual "Sincronizar" button: when the user taps it explicitly, they
 * want an attempt NOW. Without this, a report that errored seconds ago (e.g. attempt
 * 1 → 60s backoff) is skipped by getPending() and the button reports "no había
 * informes pendientes" even though one is queued.
 *
 * Only clears `lastAttemptAt` (getPending includes an error item when it has none) —
 * attempts is preserved so genuine failures still progress toward the terminal
 * "failed" state and don't loop forever on their own.
 */
export async function resetErrorBackoff(): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORE, "readwrite");
  const idx = tx.store.index("by-syncState");
  const errored = (await idx.getAll("error")) as OutboxItem[];
  for (const item of errored) {
    await tx.store.put({ ...item, lastAttemptAt: undefined });
  }
  await tx.done;
}
